import React, { useCallback } from 'react';
import { css } from '@tanium/react-emotion-9';
import { useUsingMouse } from '@tanium/react-mouse-interaction-context';
import { useControlledProp } from '@tanium/react-use-controlled-prop';
import { CollapsedCollapsibleProps, UseCollapsedReturnValue } from './types';

const noOutlineClassName = css({ ':focus': { outline: 'none' } });

/**
 * A hook for managing collapsed state of a collapsible component and props for the element
 * controlling that state.
 *
 * @see https://www.w3.org/TR/wai-aria-practices/#disclosure
 */
export const useCollapsed = <T extends CollapsedCollapsibleProps = CollapsedCollapsibleProps>(
  {
    isCollapsed: isCollapsedProp,
    defaultCollapsed = false,
    onToggleCollapsed,
    isCollapsible = true,
  }: T,
  componentName: string,
): UseCollapsedReturnValue => {
  const [isCollapsedState, setIsCollapsedState] = useControlledProp({
    componentName,
    onUpdate: ({ value }) => {
      if (onToggleCollapsed) {
        onToggleCollapsed({ isCollapsed: !!value });
      }
    },
    propName: 'isCollapsed',
    value: isCollapsedProp,
    defaultValue: defaultCollapsed,
  });

  const usingMouse = useUsingMouse();

  const isCollapsed = isCollapsedState && isCollapsible;

  const toggleCollapsedState = useCallback(() => {
    setIsCollapsedState((v) => !v);
  }, [setIsCollapsedState]);

  const collapseControlProps: Partial<React.HTMLProps<HTMLElement>> = isCollapsible
    ? {
        role: 'button',
        tabIndex: 0,
        'aria-expanded': !isCollapsed,
        onClick: () => {
          toggleCollapsedState();
        },
        onKeyPress: (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            toggleCollapsedState();
            e.preventDefault();
          }
        },
        className: usingMouse ? noOutlineClassName : undefined,
      }
    : {};

  return { isCollapsed, collapseControlProps, setIsCollapsed: setIsCollapsedState };
};
